import { FC, useEffect, useMemo, useState } from "react";
import { DaysOfTheWeek } from "../DaysOfTheWeek";
import dayjs from "dayjs";
import { TimeValidationError } from "@mui/x-date-pickers/models";
import iconBtnCloseModal from "./../../../../assets/icon-closeModal.svg";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import {
  IAvailabilities,
} from "../../../../@types/resource.type";
import { IDefaultAvailabilityType } from "../../../../@types/default-availability.type";
import { addOrUpdateDefaultAvailability } from "../../../../services/api/defaultAvailability/defaultAvailability";
import { useConfirm } from "../../../../hooks/useConfirm";
import { startResourceAvailabilityImport } from "../../../../services/api/ResourceAvailabilityImport/resourceAvailabilityImport";
import { DefaultAvailabilityStatic } from "../../../../@types/statics/default-availability.static";
import styles from "./styles.module.scss";

interface ModalAvailabilityProps {
  open: boolean;
  handleClose: () => void;
  availabilities?: IAvailabilities[] | [];
  defaultAvailabilityData?: IDefaultAvailabilityType | null;
  setAvailabilities: React.Dispatch<
    React.SetStateAction<IAvailabilities[] | []>
  >;
  setWeekHours: React.Dispatch<React.SetStateAction<number | 0>>;
}

// const formatHoursAndMinutes = (hoursDecimal: number) => {
//   const hours = Math.floor(hoursDecimal);
//   const minutes = Math.round((hoursDecimal - hours) * 60);
//   return `${hours.toString().padStart(2, "0")}h ${minutes
//     .toString()
//     .padStart(2, "0")}m`;
// };

const formatTime = (time: string | null | undefined): string | null => {
  if (!time) return null;
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  if (isoDateRegex.test(time)) {
    return new Date(time).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "America/Sao_Paulo",
    });
  }
  return time;
};

const calculateTotalTime = (
  startTime1: string | null,
  endTime1: string | null,
  startTime2: string | null,
  endTime2: string | null
): number => {
  const calculateDiff = (start: string | null, end: string | null) => {
    if (!start || !end) return 0;
    const [startHours, startMinutes] = start.split(":").map(Number);
    const [endHours, endMinutes] = end.split(":").map(Number);
    return (endHours * 60 + endMinutes - (startHours * 60 + startMinutes)) / 60;
  };

  return (
    calculateDiff(startTime1, endTime1) + calculateDiff(startTime2, endTime2)
  );
};

export const ModalAvailability: FC<ModalAvailabilityProps> = ({
  open,
  handleClose,
  availabilities,
  defaultAvailabilityData,
  setAvailabilities,
  setWeekHours,
}) => {
  const [dataAvailability, setDataAvailability] = useState<
    IAvailabilities[] | []
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    if (availabilities) {
      setDataAvailability(availabilities ?? []);
    } else {
      setDataAvailability(defaultAvailabilityData?.availabilities ?? []);
    }
  }, [availabilities, defaultAvailabilityData, open]);

  const [errorStartTime1, setErrorStartTime1] =
    useState<TimeValidationError | null>(null);
  const [errorEndTime1, setErrorEndTime1] =
    useState<TimeValidationError | null>(null);
  const [errorStartTime2, setErrorStartTime2] =
    useState<TimeValidationError | null>(null);
  const [errorEndTime2, setErrorEndTime2] =
    useState<TimeValidationError | null>(null);

  const errorMessage = useMemo(() => {
    if (
      errorStartTime1 === "maxTime" ||
      errorEndTime1 === "maxTime" ||
      errorStartTime2 === "maxTime" ||
      errorEndTime2 === "maxTime"
    ) {
      return "Informe um horário válido ";
    }

    if (
      errorStartTime1 === "minTime" ||
      errorEndTime1 === "minTime" ||
      errorStartTime2 === "minTime" ||
      errorEndTime2 === "minTime"
    ) {
      return "Informe um horário válido ";
    }

    if (
      errorStartTime1 === "invalidDate" ||
      errorEndTime1 === "invalidDate" ||
      errorStartTime2 === "invalidDate" ||
      errorEndTime2 === "invalidDate"
    ) {
      return "Informe um horário válido";
    }

    return "";
  }, [errorStartTime1, errorEndTime1, errorStartTime2, errorEndTime2]);

  const addNewField = (dayOfWeek: number) => {
    setDataAvailability((prev) =>
      prev.map((item) => {
        if (item.dayOfWeek === dayOfWeek) {
          if (item.startTime1 && item.startTime2) {
            return item;
          }

          if (!item.startTime1 || !item.endTime1) {
            return {
              ...item,
              startTime1: dayjs().hour(9).minute(0).format(),
              endTime1: dayjs().hour(13).minute(0).format(),
            };
          }

          if (!item.startTime2 || !item.endTime2) {
            if (item.startTime1 && item.endTime1) {
              return {
                ...item,
                startTime2: dayjs(`2024-01-01T${item.endTime1}`)
                  .add(1, "hour")
                  .format(),
                endTime2: dayjs(`2024-01-01T${item.endTime1}`)
                  .add(2, "hour")
                  .format(),
              };
            } else {
              return {
                ...item,
                startTime2: dayjs().hour(14).minute(0).format(),
                endTime2: dayjs().hour(18).minute(0).format(),
              };
            }
          }
        }
        return item;
      })
    );
  };

  const removeField = (dayOfWeek: number, slot: 1 | 2) => {
    setDataAvailability((prev) =>
      prev.map((item) => {
        if (item.dayOfWeek === dayOfWeek) {
          if (slot === 1) {
            setErrorStartTime1(null);
            setErrorEndTime1(null);
          } else {
            setErrorStartTime2(null);
            setErrorEndTime2(null);
          }

          const updatedItem = {
            ...item,
            ...(slot === 1 && { startTime1: null, endTime1: null }),
            ...(slot === 2 && { startTime2: null, endTime2: null }),
          };

          return {
            ...updatedItem,
            totalTime: calculateTotalTime(
              updatedItem.startTime1 ?? null,
              updatedItem.endTime1 ?? null,
              updatedItem.startTime2 ?? null,
              updatedItem.endTime2 ?? null
            ),
          };
        } else {
          return item;
        }
      })
    );
  };

  const updateField = (
    dayOfWeek: number,
    slot: 1 | 2,
    typeField: "startTime" | "endTime",
    newValue: string | null
  ) => {
    setDataAvailability((prev) =>
      prev.map((item) =>
        item.dayOfWeek === dayOfWeek
          ? {
              ...item,
              ...(slot === 1 && {
                startTime1:
                  typeField === "startTime"
                    ? formatTime(newValue)
                    : formatTime(item.startTime1),
                endTime1:
                  typeField === "endTime"
                    ? formatTime(newValue)
                    : formatTime(item.endTime1),
              }),
              ...(slot === 2 && {
                startTime2:
                  typeField === "startTime"
                    ? formatTime(newValue)
                    : formatTime(item.startTime2),
                endTime2:
                  typeField === "endTime"
                    ? formatTime(newValue)
                    : formatTime(item.endTime2),
              }),
              totalTime: calculateTotalTime(
                formatTime(
                  slot === 1 && typeField === "startTime"
                    ? newValue
                    : item.startTime1
                ),
                formatTime(
                  slot === 1 && typeField === "endTime"
                    ? newValue
                    : item.endTime1
                ),
                formatTime(
                  slot === 2 && typeField === "startTime"
                    ? newValue
                    : item.startTime2
                ),
                formatTime(
                  slot === 2 && typeField === "endTime"
                    ? newValue
                    : item.endTime2
                )
              ),
            }
          : item
      )
    );
  };

  const totalHoursPerWeek = useMemo(() => {
    return dataAvailability.reduce((total, day) => {
      let dailyTotal = 0;

      if (day.startTime1 && day.endTime1) {
        const start1 = dayjs(`2024-01-01T${day.startTime1}`);
        const end1 = dayjs(`2024-01-01T${day.endTime1}`);
        dailyTotal += end1.diff(start1, "minute") / 60;
      }

      if (day.startTime2 && day.endTime2) {
        const start2 = dayjs(`2024-01-01T${day.startTime2}`);
        const end2 = dayjs(`2024-01-01T${day.endTime2}`);
        dailyTotal += end2.diff(start2, "minute") / 60;
      }

      return total + dailyTotal;
    }, 0);
  }, [dataAvailability]);

  const handleCancel = () => {
    if (availabilities) {
      setDataAvailability(availabilities ?? []);
    } else {
      setDataAvailability(defaultAvailabilityData?.availabilities ?? []);
    }
    handleClose();
  };

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);
  const { showConfirm, Confirm } = useConfirm(
    "Deseja realmente alterar a disponibilidade padrão?",
    openModalConfirm,
    handleCloseModalConfirm,
    "Você irá alterar as disponibilidades de todos os colaboradores com essa configuração.",
    true
  );

  const handleSave = async () => {
    setLoading(true);
    if (defaultAvailabilityData) {
      handleOpenModalConfirm();
      const isConfirm = await showConfirm();
      if (isConfirm) {
        try {
          const defaultAvailabilityAPI: IDefaultAvailabilityType = {
            id: defaultAvailabilityData.id,
            name: defaultAvailabilityData.name,
            availabilities: dataAvailability,
          };
          const responseAdd = await addOrUpdateDefaultAvailability(
            defaultAvailabilityAPI
          );
          const responseStart = await startResourceAvailabilityImport(
            DefaultAvailabilityStatic.default.id
          );
          if (
            responseAdd.statusCode === 200 &&
            responseStart.statusCode === 200 &&
            responseStart.result.success === true
          ) {
            toast.success(
              "Disponibilidade Padrão atualizada em todos colaboradores com essa configuração!"
            );
            setAvailabilities(dataAvailability);
            setWeekHours(totalHoursPerWeek);
          } else {
            toast.error(
              `Não foi possível atualizar a disponibilidade padrão nos colaboradores! Tente novamente mais tarde.`
            );
          }
        } catch (error) {
          toast.error(
            `Erro: Não foi possível salvar a disponibilidade padrão!`
          );
          console.error("Erro ao atualizar disponibilidade padrão:", error);
        }
        setLoading(false);
        handleClose();
      } else {
        setLoading(false);
      }
    } else {
      setAvailabilities(dataAvailability);
      setWeekHours(totalHoursPerWeek);
      setLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCancel}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        className={styles.modal}
      >
        {loading ? (
          <CircularProgress
            size={100}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : (
          <div className={styles.wrapper}>
            <div className={styles.header}>
              <h3>Disponibilidade</h3>
              <button onClick={handleClose}>
                <img src={iconBtnCloseModal} alt="" />
              </button>
            </div>
            <div className={styles.body}>
              {dataAvailability.map((item) => (
                <DaysOfTheWeek
                  key={item.dayOfWeek}
                  infoDay={item}
                  addNewField={addNewField}
                  removeField={removeField}
                  updateField={updateField}
                  setErrorStartTime1={setErrorStartTime1}
                  setErrorEndTime1={setErrorEndTime1}
                  setErrorStartTime2={setErrorStartTime2}
                  setErrorEndTime2={setErrorEndTime2}
                />
              ))}

              {/* <p>Total Semanal: {formatHoursAndMinutes(totalHoursPerWeek)}</p> */}
              <p>Total Semanal: {totalHoursPerWeek}h</p>
            </div>
            <div className={styles.footer}>
              <p
                style={{
                  color: "#d32f2f",
                }}
              >
                {errorMessage}
              </p>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!!errorMessage}
                className={styles.btnCreate}
                onClick={handleSave}
              >
                Salvar
              </Button>
            </div>
          </div>
        )}
      </Modal>
      {Confirm}
    </>
  );
};
